import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../singleton/axios";

export interface LinkedAccount {
  id: string;
  provider: string;
  account_nickname: string;
  account_status: 'active' | 'inactive' | 'error' | 'suspended';
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: 'success' | 'error' | 'pending' | null;
  created_at: string;
  updated_at: string;
}

export interface LinkedAccountsResponse {
  success: boolean;
  accounts: LinkedAccount[];
  timestamp: string;
}

export interface LinkAccountRequest {
  provider: string;
  api_key: string;
  account_nickname?: string;
}

export interface LinkAccountResponse {
  success: boolean;
  message: string;
  account: LinkedAccount;
  initial_sync_job_id: string;
  created_at: string;
}

export interface TriggerSyncRequest {
  force_full_sync?: boolean;
}

export interface TriggerSyncResponse {
  success: boolean;
  message: string;
  sync_job_id: string;
  sync_type: 'incremental' | 'full';
  account: {
    id: string;
    provider: string;
    account_nickname: string;
    last_sync_at: string | null;
  };
  estimated_completion_time: string;
  triggered_at: string;
}

interface ApiError {
  success: false;
  error: string;
  message?: string;
  details?: string;
}

// Get all linked accounts for the authenticated user
export const useLinkedAccounts = () => {
  return useQuery<LinkedAccountsResponse, ApiError>({
    queryKey: ["linkedAccounts"],
    queryFn: async () => {
      const { data } = await api.get<LinkedAccountsResponse>("/accounts");
      return data;
    },
    refetchOnWindowFocus: true,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Get a specific linked account by ID
export const useLinkedAccount = (accountId: string) => {
  return useQuery<LinkedAccount, ApiError>({
    queryKey: ["linkedAccount", accountId],
    queryFn: async () => {
      const { data } = await api.get<{ account: LinkedAccount }>(`/accounts/${accountId}`);
      return data.account;
    },
    enabled: !!accountId,
  });
};

// Link a new account
export const useLinkAccount = () => {
  const queryClient = useQueryClient();
  
  return useMutation<LinkAccountResponse, ApiError, LinkAccountRequest>({
    mutationFn: async (linkData: LinkAccountRequest) => {
      const { data } = await api.post<LinkAccountResponse>("/accounts/link", linkData);
      return data;
    },
    onSuccess: () => {
      // Invalidate accounts list to refetch
      queryClient.invalidateQueries({ queryKey: ["linkedAccounts"] });
    },
  });
};

// Delete a linked account
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  
  return useMutation<{ success: boolean; message: string }, ApiError, string>({
    mutationFn: async (accountId: string) => {
      const { data } = await api.delete(`/accounts/${accountId}`);
      return data;
    },
    onSuccess: () => {
      // Invalidate accounts list to refetch
      queryClient.invalidateQueries({ queryKey: ["linkedAccounts"] });
    },
  });
};

// Trigger manual sync for an account
export const useTriggerSync = () => {
  const queryClient = useQueryClient();
  
  return useMutation<TriggerSyncResponse, ApiError, { accountId: string; options?: TriggerSyncRequest }>({
    mutationFn: async ({ accountId, options = {} }) => {
      const { data } = await api.post<TriggerSyncResponse>(`/accounts/${accountId}/sync`, options);
      return data;
    },
    onSuccess: () => {
      // Invalidate accounts list to refetch sync status
      queryClient.invalidateQueries({ queryKey: ["linkedAccounts"] });
    },
  });
};

// Check if user has any linked accounts for a specific provider
export const useHasLinkedAccount = (provider: string) => {
  const { data: accountsData } = useLinkedAccounts();
  
  const hasAccount = accountsData?.accounts?.some(
    account => account.provider === provider && account.account_status === 'active'
  ) ?? false;
  
  const account = accountsData?.accounts?.find(
    account => account.provider === provider && account.account_status === 'active'
  );
  
  return { hasAccount, account };
};

// Get account status summary
export const useAccountStatusSummary = () => {
  const { data: accountsData } = useLinkedAccounts();
  
  const summary = {
    total: accountsData?.accounts?.length ?? 0,
    active: accountsData?.accounts?.filter(acc => acc.account_status === 'active').length ?? 0,
    syncing: accountsData?.accounts?.filter(acc => acc.last_sync_status === 'pending').length ?? 0,
    errors: accountsData?.accounts?.filter(acc => acc.account_status === 'error').length ?? 0,
    lastSyncTimes: accountsData?.accounts
      ?.filter(acc => acc.last_sync_at)
      ?.map(acc => ({ provider: acc.provider, lastSync: acc.last_sync_at })) ?? []
  };
  
  return summary;
};